/**
 * SHARED FIXTURES — owned by the Architect/Integrator.
 *
 * Canned, contract-valid data + a FAKE simulation so Render, Shell, and Backend
 * can compile AND run standalone before Core lands. This is the thing that makes
 * the four agents truly concurrent: nobody has to wait on Core's real physics to
 * build and visually verify their own slice.
 *
 * NOT physics, NOT generation. The fake just lerps a marble forward on a scripted
 * timeline. Swap `createFakeSimulation` for `@/core`'s real `createSimulation`
 * once Core is ready — the call sites don't change because both satisfy the same
 * `Simulation` contract.
 */

import { SCHEMA_VERSION, SIM_VERSION, PROTOCOL_VERSION, TICK_RATE } from '@/types';
import type {
  CourseSpec,
  GameState,
  ReplayData,
  ScoreSubmission,
  Vec3,
} from '@/types';
import type { Simulation, SteerInput } from '@/core';

const FLAT = { friction: 0.4, restitution: 0.1 } as const;

/** A tiny but fully valid course: a straight runway with two side rails, two
 *  checkpoints (the second is the finish). Enough for Render to draw a scene. */
export const SAMPLE_COURSE: CourseSpec = {
  schemaVersion: SCHEMA_VERSION,
  simVersion: SIM_VERSION,
  // Must equal deriveDailySeed('2026-06-14') so the happy-path seed gate in the
  // Worker accepts SAMPLE_SUBMISSION (Backend flagged the old hardcoded value).
  seed: 3151915050,
  dateKey: '2026-06-14',
  bounds: { min: [-5, -2, -2], max: [5, 4, 40] },
  spawn: { position: [0, 1, 0], rotation: [0, 0, 0, 1] },
  tuning: {
    gravity: [0, -9.81, 0],
    marbleRadius: 0.5,
    marbleMass: 1,
    marbleFriction: 0.4,
    marbleRestitution: 0.2,
    steerForce: 18,
    linearDamping: 0.15,
    maxSpeed: 30,
  },
  elements: [
    { id: 0, kind: 'track', transform: { position: [0, 0, 18], rotation: [0, 0, 0, 1] }, halfExtents: [3, 0.5, 18], surface: FLAT },
    { id: 1, kind: 'wall', transform: { position: [-3, 1, 18], rotation: [0, 0, 0, 1] }, halfExtents: [0.25, 1, 18], surface: FLAT },
    { id: 2, kind: 'wall', transform: { position: [3, 1, 18], rotation: [0, 0, 0, 1] }, halfExtents: [0.25, 1, 18], surface: FLAT },
  ],
  checkpoints: [
    { index: 0, position: [0, 1, 18], radius: 3, isFinish: false },
    { index: 1, position: [0, 1, 36], radius: 3, isFinish: true },
  ],
};

export const SAMPLE_GAME_STATE: GameState = {
  tick: 0,
  status: 'running',
  marble: SAMPLE_COURSE.spawn,
  velocity: [0, 0, 0],
  checkpointIndex: 0,
  elapsedMs: 0,
};

export const SAMPLE_REPLAY: ReplayData = {
  schemaVersion: SCHEMA_VERSION,
  simVersion: SIM_VERSION,
  seed: SAMPLE_COURSE.seed,
  dateKey: SAMPLE_COURSE.dateKey,
  tickRate: TICK_RATE,
  totalTicks: 600,
  inputEvents: [
    { tick: 0, steerX: 0, steerY: 32767 },
    { tick: 120, steerX: 8000, steerY: 32767 },
    { tick: 240, steerX: 0, steerY: 32767 },
  ],
  metrics: { finishTick: 600, finishTimeMs: 10000, checkpointsReached: 2, distance: 36, score: 90000 },
  stateHash: 'fixture-deadbeef',
};

export const SAMPLE_SUBMISSION: ScoreSubmission = {
  protocolVersion: PROTOCOL_VERSION,
  dateKey: SAMPLE_COURSE.dateKey,
  seed: SAMPLE_COURSE.seed,
  score: SAMPLE_REPLAY.metrics.score,
  replay: SAMPLE_REPLAY,
  client: {
    clientVersion: 'fixture',
    protocolVersion: PROTOCOL_VERSION,
    platform: 'web',
    userAgent: 'fixture',
    timezoneOffsetMin: 0,
    submittedAt: '2026-06-14T00:00:00.000Z',
  },
};

/**
 * A stand-in Simulation that satisfies the Core contract without any physics.
 * Marble drifts forward in +Z at a steer-nudged constant rate; finishes when it
 * reaches the finish checkpoint's Z. Lets Shell run a real game loop and Render
 * draw real motion today. Replace with `@/core` `createSimulation` later.
 */
export function createFakeSimulation(spec: CourseSpec = SAMPLE_COURSE): Simulation {
  const finish = spec.checkpoints[spec.checkpoints.length - 1];
  const start = spec.spawn.position;
  let tick = 0;
  let pos: Vec3 = start;
  let status: GameState['status'] = 'running';
  let checkpointIndex = 0;

  const snapshot = (): GameState => ({
    tick,
    status,
    marble: { position: pos, rotation: spec.spawn.rotation },
    velocity: [0, 0, status === 'running' ? 6 : 0],
    checkpointIndex,
    elapsedMs: (tick / TICK_RATE) * 1000,
  });

  return {
    spec,
    step(input: SteerInput): GameState {
      if (status === 'running') {
        tick += 1;
        pos = [pos[0] + input.steerX * 0.05, pos[1], pos[2] + 0.1];
        if (checkpointIndex < spec.checkpoints.length &&
            pos[2] >= spec.checkpoints[checkpointIndex].position[2]) {
          checkpointIndex += 1;
        }
        if (pos[2] >= finish.position[2]) status = 'finished';
      }
      return snapshot();
    },
    getState: snapshot,
    getReplay: (): ReplayData => ({ ...SAMPLE_REPLAY, totalTicks: tick }),
    reset(): void {
      tick = 0;
      pos = start;
      status = 'running';
      checkpointIndex = 0;
    },
  };
}
